import React from "react";
import { motion } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

const STEPS = [
    {
        n: "01",
        title: "Source",
        text: "Still-drawn spring water, filtered six times through volcanic stone.",
    },
    {
        n: "02",
        title: "Freeze",
        text: "Seventy-two hours of directional freezing. Impurities retreat. Clarity remains.",
    },
    {
        n: "03",
        title: "Carve",
        text: "Each block hand-cut by our master ice sculptor with a hot brass wire to exact millimetres.",
    },
    {
        n: "04",
        title: "Deliver",
        text: "Couriered at −18°C to your door within twelve hours of carving.",
    },
];

export default function Craftsmanship() {
    return (
        <section
            id="craft"
            data-testid="craft-section"
            className="relative bg-obsidian-800 py-24 md:py-32 lg:py-40 overflow-hidden"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    {/* Left — text */}
                    <div className="lg:col-span-6 lg:sticky lg:top-32">
                        <motion.span
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, ease }}
                            className="overline"
                        >
                            § 01 — The Craft
                        </motion.span>

                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, ease, delay: 0.1 }}
                            className="mt-6 font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter"
                        >
                            The element <br />
                            of <span className="italic text-amber-glow">time</span>.
                        </motion.h2>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, ease, delay: 0.25 }}
                            className="mt-8 text-ice-200/70 leading-relaxed max-w-lg"
                        >
                            A cube from your freezer is rushed. Cloudy, fractured, and already fighting you.
                            Ours is made slowly — water released of its impurities, one molecule at a time,
                            until it no longer has anything to say. Silence, in crystal.
                        </motion.p>

                        <div className="mt-12 space-y-6 max-w-lg">
                            {STEPS.map((s, i) => (
                                <motion.div
                                    key={s.n}
                                    data-testid={`craft-step-${s.n}`}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.8, ease, delay: i * 0.08 }}
                                    className="flex gap-6 pb-6 border-b border-white/10"
                                >
                                    <span className="font-serif-display italic text-amber-glow text-2xl w-12 shrink-0">
                                        {s.n}
                                    </span>
                                    <div>
                                        <h4 className="font-serif-display text-xl text-ice-50">
                                            {s.title}
                                        </h4>
                                        <p className="text-ice-200/60 text-sm mt-1 leading-relaxed">
                                            {s.text}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Right — stacked images */}
                    <div className="lg:col-span-6 relative min-h-[600px]">
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.1, ease }}
                            className="relative aspect-[4/5] w-full max-w-md ml-auto overflow-hidden"
                        >
                            <img
                                src="https://images.pexels.com/photos/434259/pexels-photo-434259.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900"
                                alt="Close-up of clear ice reflections showing the handcrafted quality"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 ring-1 ring-white/10" />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.1, ease, delay: 0.2 }}
                            className="relative aspect-[3/4] w-60 md:w-72 -mt-24 md:-mt-32 overflow-hidden"
                        >
                            <img
                                src="https://images.pexels.com/photos/34927441/pexels-photo-34927441.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700"
                                alt="A stack of perfectly clear ice cubes"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 ring-1 ring-white/10" />
                        </motion.div>

                        {/* Floating script */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, delay: 0.6 }}
                            className="absolute bottom-10 right-0 md:right-10 font-script text-amber-glow text-4xl md:text-5xl rotate-[-4deg]"
                        >
                            crystalline
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
